import type { FindingSeverity } from '@services/auditService';

/**
 * How a finding's severity is presented, in one place.
 *
 * REV-720. Four copies of this decision existed: an identical
 * `severityChipColor` in `OrgFindingsPage` and `FindingDetailPage`, and two
 * `SEVERITY_COLOR` records in `TestRunDetailPage` and `components/audit/
 * FindingsTab`. A ticket about two tables disagreeing is a poor place to leave
 * four spellings of the same mapping, so it lives here — a util rather than a
 * component module, so a page that wants the colour does not have to import a
 * table to get it.
 *
 * `FindingsTab` and `TestRunDetailPage` still carry their own; folding them in
 * belongs with the scan-results repoint, which is already rewriting one of them.
 */
export function severityChipColor(sev: FindingSeverity): 'error' | 'warning' | 'info' | 'default' {
  if (sev === 'critical') return 'error';
  if (sev === 'major') return 'warning';
  if (sev === 'minor') return 'info';
  return 'default';
}

/**
 * Last-resort English labels, used only when a translation is missing.
 *
 * Deliberately not the stored value: `f.severity` as a default is what made the
 * findings table read "critical" while every other surface said "Critical". A
 * fallback should degrade to something a reader recognises, not to a database
 * column.
 */
export const SEVERITY_FALLBACK: Record<FindingSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};
